import { getDeployment } from 'utils/deployment';

export const RBAC_ACTIONS = Object.freeze({
    SHOW: 'CanShow',
    CREATE: 'CanCreate',
    UPDATE: 'CanUpdate',
    DELETE: 'CanDelete',
    INVITE: 'CanInvite',
    APPROVE_APPEAL: 'CanApproveAppeal',
    SHOW_ORG_SETTINGS: 'CanShowOrgSettings',
    UPDATE_ORG_SETTINGS: 'CanUpdateOrgSettings',
    SHOW_MANAGER_USERS: 'CanShowManageUsers',
    CREATE_MANAGE_USERS: 'CanCreateManageUsers',
    UPDATE_MANAGE_USERS: 'CanUpdateMangeUsers',
    SHOW_NOTIF_SETTINGS: 'CanShowNotifSettings',
    UPDATE_NOTIF_SETTINGS: 'CanUpdateNotifSettings',
    SHOW_AUDIT_LOGS: 'CanShowAuditLogs',
    ALL: '*'
});

export const RBAC_RESOURCES = Object.freeze({
    DASHBOARD: 'dashboard',
    CANDIDATES: 'candidates',
    ASSESSMENTS: 'assessments',
    COURSES: 'courses',
    REPORTS: 'reports',
    SETTINGS: 'settings',
    USERS: 'users',
    UNITS: 'units'
});

const ADMIN_ROLE_NAMES = new Set(['administrator', 'admin', 'super administrator', 'super admin']);
const ENROLMENT_ROLE_NAMES = new Set(['enrolment staff', 'enrollment staff', 'enrolment officer']);
const ASSESSOR_ROLE_NAMES = new Set(['assessor']);
const COMPLIANCE_MANAGER_ROLE_NAMES = new Set(['compliance manager', 'compliance_manager']);
const AUDITOR_ROLE_NAMES = new Set(['auditor']);
const ARN_PREFIX = 'arn:';
const DEFAULT_RESOURCE_ID = '*';

const isFullArn = (value = '') => String(value).startsWith(ARN_PREFIX);

export const buildRbacResourcePath = (resource, resourceId = DEFAULT_RESOURCE_ID) => {
    const resourcePath = String(resource || '').trim();

    if (!resourcePath) {
        throw new Error('RBAC resource is required');
    }

    if (isFullArn(resourcePath)) {
        return resourcePath;
    }

    if (resourcePath.includes('/') || resourcePath.endsWith('*')) {
        return resourcePath;
    }

    return `${resourcePath}/${resourceId || DEFAULT_RESOURCE_ID}`;
};

export const buildRbacResource = (resource, resourceId = DEFAULT_RESOURCE_ID) => {
    if (isFullArn(resource)) {
        return resource;
    }

    return `arn:tlrg:rpl-robot::${getDeployment()}:${buildRbacResourcePath(resource, resourceId)}`;
};

export const normalizeRoleName = (name = '') => String(name).trim().toLowerCase();

export const isAdministratorRoleName = (name = '') => ADMIN_ROLE_NAMES.has(normalizeRoleName(name));

export const getRoleNames = (user = {}) => (user?.roles || []).map((role) => normalizeRoleName(role?.name)).filter(Boolean);

export const hasRoleName = (user = {}, roleNames = []) => {
    const userRoleNames = getRoleNames(user);
    return roleNames.some((roleName) => userRoleNames.includes(normalizeRoleName(roleName)));
};

export const isAdmin = (user = {}) => getRoleNames(user).some((roleName) => ADMIN_ROLE_NAMES.has(roleName));

export const isEnrolmentStaff = (user = {}) => getRoleNames(user).some((roleName) => ENROLMENT_ROLE_NAMES.has(roleName));

export const isAdminOrEnrolment = (user = {}) => isAdmin(user) || isEnrolmentStaff(user);

export const isAssessor = (user = {}) => getRoleNames(user).some((roleName) => ASSESSOR_ROLE_NAMES.has(roleName));

export const isComplianceManager = (user = {}) => getRoleNames(user).some((roleName) => COMPLIANCE_MANAGER_ROLE_NAMES.has(roleName));

export const isAuditor = (user = {}) => getRoleNames(user).some((roleName) => AUDITOR_ROLE_NAMES.has(roleName));

export const hasUsableStatements = (role = {}) =>
    Array.isArray(role.Statement) &&
    role.Statement.some(
        (statement) =>
            ['Allow', 'Deny'].includes(statement?.Effect) &&
            Array.isArray(statement?.Action) &&
            statement.Action.length > 0 &&
            Boolean(statement?.Resource)
    );
